import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import styled from 'styled-components';
import { Carrera, Socio } from '../../models/types';

const DashboardContainer = styled.div`
  margin-top: 40px;
  padding: 0;
  background: none;
`;

const CollapseButton = styled.button`
  width: 100%;
  background: #0f3460;
  color: #fff;
  border: none;
  border-radius: 12px 12px 0 0;
  font-size: 1.15em;
  font-weight: 600;
  padding: 18px 0;
  cursor: pointer;
  margin-bottom: 0;
  letter-spacing: 0.02em;
  transition: background 0.2s;
  &:hover { background: #143d6b; }
`;

const CollapseContent = styled.div<{ open: boolean }>`
  max-height: ${({ open }) => (open ? '3000px' : '0')};
  overflow: hidden;
  transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 2px 16px rgba(15, 52, 96, 0.08);
  padding: ${({ open }) => (open ? '32px 32px 40px 32px' : '0 32px')};
`;

const StatsCardsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 36px;
  width: 100%;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  flex: 1 1 180px;
  min-width: 160px;
  background: #e2f0ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 18px;
  box-shadow: 0 1px 4px rgba(15,52,96,0.06);
`;

const StatIcon = styled.span`
  font-size: 2.1em;
  color: #0f3460;
  display: flex;
  align-items: center;
`;

const StatValue = styled.span`
  font-size: 2em;
  font-weight: bold;
  color: #0f3460;
  margin-right: 6px;
`;

const StatLabel = styled.span`
  font-size: 1.08em;
  color: #5A6178;
  font-weight: 500;
`;

const SectionTitle = styled.h3`
  color: #0f3460;
  margin-bottom: 24px;
  font-size: 1.35em;
  letter-spacing: 0.01em;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 32px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  background: #e2f0ff;
  color: #0f3460;
  padding: 8px 12px;
  border-bottom: 1px solid #d0e2ff;
  font-size: 0.97em;
`;

const Td = styled.td`
  padding: 8px 12px;
  text-align: center;
  border-bottom: 1px solid #f0f0f0;
`;

const PilotoMiniCard = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  padding: 10px 18px;
  margin-bottom: 12px;
`;

const PilotoFoto = styled.img`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 14px;
  border: 2px solid #e2f0ff;
`;

const PilotoInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const PilotoNombre = styled.span`
  font-weight: bold;
  color: #0f3460;
  font-size: 1.1em;
`;

const PilotoStats = styled.span`
  color: #5A6178;
  font-size: 0.98em;
`;

const CORPORATE_COLORS = [
  '#0f3460', // azul oscuro
  '#5A6178', // gris medio
  '#e2f0ff', // azul claro
  '#b0c4de', // gris azulado
  '#a3b1c6', // gris suave
  '#dbeafe', // azul muy claro
  '#bfc9d9'  // gris azulado claro
];

export interface PilotoCarreraPuntos {
  pilotoId: string;
  nombre: string;
  foto: string;
  puntosPorCarrera: { carreraId: string; carreraNombre: string; puntos: number; }[];
  total: number;
  victorias: number;
  podios: number;
  poles: number;
  vueltasRapidas: number;
  sanciones: number;
}

interface EquipoEstadisticasDashboardProps {
  pilotos: PilotoCarreraPuntos[];
  carreras: Carrera[];
  equipoNombre: string;
}

const EquipoEstadisticasDashboard: React.FC<EquipoEstadisticasDashboardProps> = ({ pilotos, carreras, equipoNombre }) => {
  // Datos para gráficos
  const barData = pilotos.map(p => ({ nombre: p.nombre, puntos: p.total }));
  const pieData = pilotos.map(p => ({ name: p.nombre, value: p.total }));
  const lineData = carreras.map((c, idx) => {
    let total = 0;
    pilotos.forEach(p => {
      const entry = p.puntosPorCarrera.find(e => e.carreraId === c.id);
      if (entry) total += entry.puntos;
    });
    return { carrera: c.nombre, puntos: total };
  });

  // Estadísticas agregadas
  const totalVictorias = pilotos.reduce((sum, p) => sum + p.victorias, 0);
  const totalPodios = pilotos.reduce((sum, p) => sum + p.podios, 0);
  const totalPoles = pilotos.reduce((sum, p) => sum + p.poles, 0);
  const totalVR = pilotos.reduce((sum, p) => sum + p.vueltasRapidas, 0);
  const totalSanciones = pilotos.reduce((sum, p) => sum + p.sanciones, 0);

  const [open, setOpen] = useState(false);
  return (
    <>
      <DashboardContainer>
        <CollapseButton onClick={() => setOpen(o => !o)}>
          {open ? `▼ Estadísticas del equipo ${equipoNombre}` : `► Estadísticas del equipo ${equipoNombre}`}
        </CollapseButton>
        <CollapseContent open={open}>
          <StatsCardsRow>
            <StatCard><StatIcon>🏆</StatIcon><div><StatValue>{totalVictorias}</StatValue><StatLabel>Victorias</StatLabel></div></StatCard>
            <StatCard><StatIcon>🥉</StatIcon><div><StatValue>{totalPodios}</StatValue><StatLabel>Podios</StatLabel></div></StatCard>
            <StatCard><StatIcon>⏱️</StatIcon><div><StatValue>{totalVR}</StatValue><StatLabel>Vueltas rápidas</StatLabel></div></StatCard>
            <StatCard><StatIcon>🎯</StatIcon><div><StatValue>{totalPoles}</StatValue><StatLabel>Poles</StatLabel></div></StatCard>
            <StatCard><StatIcon>⚠️</StatIcon><div><StatValue>{totalSanciones}</StatValue><StatLabel>Sanciones</StatLabel></div></StatCard>
          </StatsCardsRow>
          <div style={{ width: '100%', maxWidth: 800, margin: '0 auto', marginBottom: 44 }}>
            <div style={{ marginBottom: 40, background: '#f7faff', borderRadius: 12, padding: 32 }}>
              <div style={{ fontWeight: 600, color: '#0f3460', fontSize: 17, marginBottom: 16 }}>Puntos totales por piloto</div>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={barData} barSize={48} margin={{ top: 24, right: 24, left: 0, bottom: 24 }}>
                  <XAxis dataKey="nombre" tick={{ fill: '#5A6178', fontWeight: 500 }} axisLine={{ stroke: '#e2f0ff' }} tickLine={false} />
                  <YAxis tick={{ fill: '#5A6178', fontWeight: 500 }} axisLine={{ stroke: '#e2f0ff' }} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2f0ff', color: '#0f3460' }} />
                  <Bar dataKey="puntos" radius={[10, 10, 0, 0]} fill="#0f3460">
                    {barData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={CORPORATE_COLORS[idx % CORPORATE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ marginBottom: 40, background: '#f7faff', borderRadius: 12, padding: 32 }}>
              <div style={{ fontWeight: 600, color: '#0f3460', fontSize: 17, marginBottom: 16 }}>Distribución de puntos por piloto</div>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={{ fill: '#0f3460', fontWeight: 500 }}>
                    {pieData.map((entry, idx) => (
                      <Cell key={`cell-pie-${idx}`} fill={CORPORATE_COLORS[idx % CORPORATE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2f0ff', color: '#0f3460' }} />
                  <Legend iconType="circle" layout="horizontal" align="center" verticalAlign="bottom" wrapperStyle={{ color: '#5A6178', fontWeight: 500, marginTop: 10 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ marginBottom: 40, background: '#f7faff', borderRadius: 12, padding: 32 }}>
              <div style={{ fontWeight: 600, color: '#0f3460', fontSize: 17, marginBottom: 16 }}>Puntos totales del equipo por carrera</div>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={lineData} margin={{ top: 24, right: 24, left: 0, bottom: 24 }}>
                  <XAxis dataKey="carrera" tick={{ fill: '#5A6178', fontWeight: 500 }} axisLine={{ stroke: '#e2f0ff' }} tickLine={false} />
                  <YAxis tick={{ fill: '#5A6178', fontWeight: 500 }} axisLine={{ stroke: '#e2f0ff' }} tickLine={false} />
                  <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e2f0ff', color: '#0f3460' }} />
                  <Line type="monotone" dataKey="puntos" stroke="#0f3460" strokeWidth={3} dot={{ r: 5, fill: '#e2f0ff', stroke: '#0f3460', strokeWidth: 2 }} activeDot={{ r: 7, fill: '#0f3460' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <SectionTitle>Puntos por piloto y carrera</SectionTitle>
          <Table>
            <thead>
              <tr>
                <Th>Piloto</Th>
                {carreras.map(c => <Th key={c.id}>{c.nombre}</Th>)}
                <Th>Total</Th>
                <Th>Victorias</Th>
                <Th>Podios</Th>
                <Th>Poles</Th>
                <Th>VR</Th>
                <Th>Sanciones</Th>
              </tr>
            </thead>
            <tbody>
              {pilotos.map(p => (
                <tr key={p.pilotoId}>
                  <Td><PilotoMiniCard><PilotoFoto src={p.foto}/><PilotoInfo><PilotoNombre>{p.nombre}</PilotoNombre></PilotoInfo></PilotoMiniCard></Td>
                  {carreras.map(c => {
                    const entry = p.puntosPorCarrera.find(e => e.carreraId === c.id);
                    return <Td key={c.id}>{entry ? entry.puntos : '-'}</Td>;
                  })}
                  <Td>{p.total}</Td>
                  <Td>{p.victorias}</Td>
                  <Td>{p.podios}</Td>
                  <Td>{p.poles}</Td>
                  <Td>{p.vueltasRapidas}</Td>
                  <Td>{p.sanciones}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </CollapseContent>
      </DashboardContainer>
    </>);
};

export default EquipoEstadisticasDashboard;
